import api from './api.service';
import type { VoucherPrintData } from './salesInvoice.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface SupplierAdvanceRequestDTO {
  id: string;
  advance_request_number: string;
  purchase_order_id: string;
  supplier_id: string;
  currency: string;
  requested_amount: number;
  paid_amount: number;
  adjusted_amount: number;
  refunded_amount?: number;
  recovery_status?: string | null;
  reason?: string;
  requested_date: string;
  remarks?: string;
  status: string;
  department_id?: string | null;
  approval_instance_id?: string | null;
  purchaseOrder?: {
    po_number?: string;
    project_id?: string;
    project?: { id?: string; project_number?: string; project_name?: string };
  };
  supplier?: { supplier_name?: string };
}

export interface SarInvoiceAdjustment {
  payment_voucher_id: string;
  voucher_number: string;
  supplier_invoice_id: string;
  invoice_number: string;
  supplier_invoice_number?: string | null;
  currency: string;
  allocated_amount: number;
  adjustment_date: string;
}

export interface SupplierAdvanceRequestPrintData {
  sar: SupplierAdvanceRequestDTO & {
    purchaseOrder?: Record<string, unknown>;
    supplier?: Record<string, unknown>;
  };
  purchase_order: {
    id: string;
    po_number: string;
    po_date?: string;
    po_status?: string;
    supplier_name?: string;
    supplier_address?: string;
    supplier_contact?: string;
    currency?: string;
    grand_total?: number;
    payment_terms?: string;
    delivery_terms?: string;
    advance_required?: boolean;
    advance_payment_percentage?: number;
    advance_payment_amount?: number;
    advance_type?: string;
    advance_reason?: string;
    expected_advance_date?: string;
    advance_payment_terms?: string;
    advance_approval_required?: boolean;
    advance_status?: string;
    project_id?: string | null;
    project_number?: string | null;
    project_name?: string | null;
  } | null;
  supplier: {
    supplier_code?: string;
    supplier_name?: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    country?: string;
    tax_registration_number?: string;
  } | null;
  po_summary: {
    po_id: string;
    po_number: string;
    advance_cap: number;
    requested: number;
    paid: number;
    adjusted: number;
    remaining: number;
    unpaid_request_balance: number;
    advance_status?: string;
    requests?: Array<{
      id: string;
      advance_request_number: string;
      status: string;
      requested_amount: number;
      paid_amount: number;
      adjusted_amount: number;
    }>;
  };
  amounts: {
    requested: number;
    paid: number;
    adjusted: number;
    balance_to_pay: number;
    open_advance: number;
  };
  payments: Array<{
    id: string;
    voucher_number: string;
    voucher_date: string;
    amount: number;
    currency: string;
    payment_mode: string;
    status: string;
    journal_voucher_id?: string | null;
    can_print_voucher?: boolean;
    cheque_number?: string;
    bank_name?: string;
    transaction_reference?: string;
    narration?: string;
  }>;
  adjustments: SarInvoiceAdjustment[];
}

export interface SarLinkedPaymentVoucher {
  id: string;
  voucher_number: string;
  voucher_date: string;
  amount: number;
  currency: string;
  payment_mode: string;
  status: string;
  journal_voucher_id: string | null;
  can_print_voucher: boolean;
  cheque_number?: string;
  bank_name?: string;
  transaction_reference?: string;
  narration?: string;
}

export interface SarPrintVoucherResponse {
  advance_request_number: string;
  payment_voucher: {
    id: string;
    voucher_number: string;
    voucher_date: string;
    amount: number;
    currency: string;
    payment_mode: string;
    status: string;
  };
  /** Advance booking only: Dr Advances / Cr AP. Bank payment prints from Payment Voucher. */
  journal_voucher: VoucherPrintData;
}

export interface PayableSarForPayment {
  id: string;
  advance_request_number: string;
  purchase_order_id: string;
  po_number: string | null;
  po_milestone_id: string | null;
  currency: string;
  requested_amount: number;
  paid_amount: number;
  remaining_amount: number;
  status: string;
}

export const supplierAdvanceRequestService = {
  async list(params?: { purchase_order_id?: string; supplier_id?: string; status?: string }) {
    const res = await api.get('/procurement/supplier-advance-requests', { params });
    return unwrap<SupplierAdvanceRequestDTO[]>(res);
  },
  async listPayableForPayment(supplierId: string, currency: string) {
    const res = await api.get('/procurement/supplier-advance-requests/payable-for-payment', {
      params: { supplier_id: supplierId, currency },
    });
    return unwrap<PayableSarForPayment[]>(res);
  },
  async getById(id: string) {
    const res = await api.get(`/procurement/supplier-advance-requests/${id}`);
    return unwrap<SupplierAdvanceRequestDTO>(res);
  },
  async getPrintData(id: string) {
    const res = await api.get(`/procurement/supplier-advance-requests/${id}/print`);
    return unwrap<SupplierAdvanceRequestPrintData>(res);
  },
  async getInvoiceAdjustments(id: string) {
    const res = await api.get(`/procurement/supplier-advance-requests/${id}/invoice-adjustments`);
    return unwrap<SarInvoiceAdjustment[]>(res);
  },
  async getLinkedPaymentVouchers(id: string) {
    const res = await api.get(`/procurement/supplier-advance-requests/${id}/payment-vouchers`);
    return unwrap<SarLinkedPaymentVoucher[]>(res);
  },
  async getPrintVoucherData(sarId: string, paymentVoucherId: string) {
    const res = await api.get(
      `/procurement/supplier-advance-requests/${sarId}/print-voucher?payment_voucher_id=${encodeURIComponent(paymentVoucherId)}`
    );
    return unwrap<SarPrintVoucherResponse>(res);
  },
  async createFromPo(poId: string, body: Record<string, unknown>) {
    const res = await api.post(`/procurement/purchase-orders/${poId}/advance-requests`, body);
    return unwrap<SupplierAdvanceRequestDTO>(res);
  },
  async submit(id: string) {
    const res = await api.post(`/procurement/supplier-advance-requests/${id}/submit`, {});
    return unwrap<SupplierAdvanceRequestDTO>(res);
  },
  async cancel(id: string) {
    const res = await api.post(`/procurement/supplier-advance-requests/${id}/cancel`, {});
    return unwrap<SupplierAdvanceRequestDTO>(res);
  },
  async createPaymentVoucher(id: string, body: Record<string, unknown>) {
    const res = await api.post(`/procurement/supplier-advance-requests/${id}/payment-voucher`, body);
    return unwrap<{ id: string; voucher_number: string }>(res);
  },
};
